import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, forkJoin, map, of, switchMap } from "rxjs";
import { ChargePointOverview, ChargePointsOverviewFilter } from "@app/models/chargeprice/charge-point-overview.model";
import { PaginatedList } from "@app/models/common/paginated-list.class";
import { environment } from "@environments/environment";

@Injectable()
export class ChargePriceService {

  /** Largest page size accepted by the charge price service */
  static readonly maxPageSize = 1000;

  private uri: string = `${environment.routes.apiEndpoint}/charge-price/v1`;

  constructor(private http: HttpClient) { }

  getChargePointsOverview(
    filter: ChargePointsOverviewFilter = {},
    pageIndex: number = 1,
    pageSize: number = ChargePriceService.maxPageSize,
    sortByField: string = 'LastModified',
    sortDescending: boolean = true
  ) {
    let params = new HttpParams()
      .set('pageIndex', pageIndex)
      .set('pageSize', pageSize)
      .set('sortByField', sortByField)
      .set('sortDescending', sortDescending);

    filter.locationIds?.forEach(id => params = params.append('locationIds', id));
    filter.connectorStatuses?.forEach(status => params = params.append('connectorStatuses', status));
    filter.priceGroupTypes?.forEach(type => params = params.append('priceGroupTypes', type));

    return this.http.get<PaginatedList<ChargePointOverview>>(`${this.uri}/charge-points/overview`, { params });
  }

  /** Fetches every page of the charge point overview, so the result can be grouped per location */
  getAllChargePointsOverview(filter: ChargePointsOverviewFilter = {}): Observable<ChargePointOverview[]> {
    return this.getChargePointsOverview(filter, 1).pipe(
      switchMap(firstPage => {
        if (firstPage.totalPages <= 1) return of(firstPage.items ?? []);

        const remainingPages = Array.from({ length: firstPage.totalPages - 1 }, (_, index) =>
          this.getChargePointsOverview(filter, index + 2));

        return forkJoin(remainingPages).pipe(
          map(pages => [firstPage, ...pages].flatMap(page => page.items ?? []))
        );
      })
    );
  }
}
